import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, Trash2, MapPin, Pencil, Feather } from "lucide-react";
import Button from "../../components/ui/Button";
import { LOCATION_CATEGORIES } from "./api";
import { CategoryIcon } from "./PinIcon";

function ModalShell({ title, subtitle, onClose, children, wide, testId }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6 bg-background/80 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid={testId || "cartomancer-dialog"}
    >
      <div
        className={`rune-border bg-card rounded-lg w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-hidden flex flex-col`}
      >
        <div className="flex items-start justify-between p-5 border-b border-border">
          <div>
            <h3 className="font-display text-xl text-gold">{title}</h3>
            {subtitle && (
              <p className="font-serif-fantasy italic text-sm text-muted-foreground">{subtitle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md border border-gold/30 text-muted-foreground hover:text-gold hover:border-gold hover:bg-gold/10 active:bg-gold/20 transition-all"
            data-testid="dialog-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto scrollbar-thin">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, testId }) {
  return (
    <div data-testid={testId}>
      <label className="block font-heading text-parchment text-[10px] tracking-widest uppercase mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

/* ---------- LOCATION DIALOG (edit mode) ---------- */

export function LocationDialog({ location, onClose, onSave, onDelete }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    category: "city",
    description: "",
    npcs: "",
    treasures: "",
    hooks: "",
    dm_notes: "",
    ...location,
  });

  useEffect(() => setForm((f) => ({ ...f, ...location })), [location?.id]); // eslint-disable-line

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <ModalShell
      title={location?.id ? t("carto.dialogLocationEditTitle") : t("carto.dialogLocationNewTitle")}
      subtitle={t("carto.dialogLocationSubtitle")}
      onClose={onClose}
      testId="location-dialog"
    >
      <div className="space-y-4">
        <Field label={t("carto.fieldName")}>
          <input
            className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm focus:outline-none focus:border-gold"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            autoFocus
            data-testid="location-name-input"
          />
        </Field>

        <Field label={t("carto.fieldCategory")}>
          <div className="grid grid-cols-4 sm:grid-cols-7 gap-2" data-testid="location-cats">
            {LOCATION_CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => update("category", c.id)}
                className={`p-2 rounded border text-[9px] font-heading tracking-widest uppercase transition-all flex flex-col items-center gap-1 ${
                  form.category === c.id
                    ? "border-gold bg-gold/25 text-gold shadow-inner scale-105"
                    : "border-border hover:border-gold/60 hover:bg-gold/10 hover:text-gold text-muted-foreground"
                }`}
                data-testid={`location-cat-${c.id}`}
              >
                <CategoryIcon id={c.id} className="w-4 h-4" />
                {c.label}
              </button>
            ))}
          </div>
        </Field>

        <Field label={t("carto.fieldDescription")}>
          <textarea
            rows={3}
            className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm focus:outline-none focus:border-gold"
            value={form.description || ""}
            onChange={(e) => update("description", e.target.value)}
            data-testid="location-desc-input"
          />
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t("carto.fieldNpcs")}>
            <textarea
              rows={2}
              className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm focus:outline-none focus:border-gold"
              value={form.npcs || ""}
              onChange={(e) => update("npcs", e.target.value)}
              data-testid="location-npcs-input"
            />
          </Field>
          <Field label={t("carto.fieldTreasures")}>
            <textarea
              rows={2}
              className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm focus:outline-none focus:border-gold"
              value={form.treasures || ""}
              onChange={(e) => update("treasures", e.target.value)}
              data-testid="location-treasures-input"
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t("carto.fieldHooks")}>
            <textarea
              rows={2}
              className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm focus:outline-none focus:border-gold"
              value={form.hooks || ""}
              onChange={(e) => update("hooks", e.target.value)}
              data-testid="location-hooks-input"
            />
          </Field>
          <Field label={t("carto.fieldDmNotes")}>
            <textarea
              rows={2}
              className="w-full bg-input border border-gold/30 rounded px-3 py-2 text-sm focus:outline-none focus:border-gold"
              value={form.dm_notes || ""}
              onChange={(e) => update("dm_notes", e.target.value)}
              data-testid="location-dm-notes-input"
            />
          </Field>
        </div>

        <div className="flex items-center justify-between pt-4 gap-2">
          {location?.id ? (
            <Button variant="destructive" size="sm" onClick={() => onDelete?.(location)} data-testid="location-delete-btn">
              <Trash2 className="w-4 h-4 mr-2" /> {t("carto.delete")}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={onClose}>
              {t("carto.cancel")}
            </Button>
            <Button variant="primary" size="sm" onClick={() => onSave(form)} data-testid="location-save-btn">
              <MapPin className="w-4 h-4 mr-2" /> {t("carto.save")}
            </Button>
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

/* ---------- LOCATION INFO PANEL (view first, then Modifica) ---------- */

export function LocationInfoPanel({ location, onClose, onEdit }) {
  const { t } = useTranslation();
  if (!location) return null;
  const rows = [
    { key: "description", label: t("carto.labelDescription"), value: location.description },
    { key: "npcs", label: t("carto.labelNpcs"), value: location.npcs },
    { key: "treasures", label: t("carto.labelTreasures"), value: location.treasures },
    { key: "hooks", label: t("carto.labelHooks"), value: location.hooks },
    { key: "dm_notes", label: t("carto.labelDmNotes"), value: location.dm_notes },
  ].filter((r) => (r.value || "").trim().length > 0);

  return (
    <div
      className="absolute top-4 right-4 z-30 w-80 max-h-[75vh] rune-border bg-card/95 backdrop-blur rounded-lg overflow-hidden flex flex-col"
      data-testid="location-info-panel"
    >
      <div className="flex items-start justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2 min-w-0">
          <CategoryIcon id={location.category} className="w-5 h-5 shrink-0" />
          <div className="min-w-0">
            <h4 className="font-display text-lg text-gold truncate">{location.name}</h4>
            <p className="font-heading text-[10px] tracking-widest text-muted-foreground uppercase">
              {(location.category || "city").toUpperCase()}
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md border border-gold/30 text-muted-foreground hover:text-gold hover:border-gold hover:bg-gold/10 active:bg-gold/20 transition-all"
          data-testid="info-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="p-4 overflow-y-auto scrollbar-thin flex-1 space-y-3">
        {rows.length === 0 ? (
          <p className="text-xs italic text-muted-foreground">{t("carto.infoEmpty")}</p>
        ) : (
          rows.map((r) => (
            <div key={r.key}>
              <p className="font-heading text-[9px] tracking-widest text-gold uppercase mb-1">{r.label}</p>
              <p className="text-xs text-parchment whitespace-pre-line">{r.value}</p>
            </div>
          ))
        )}
      </div>
      <div className="p-3 border-t border-border">
        <Button variant="primary" size="sm" className="w-full" onClick={onEdit} data-testid="location-edit-btn">
          <Pencil className="w-4 h-4 mr-2" /> {t("carto.edit")}
        </Button>
      </div>
    </div>
  );
}

/* ---------- PREMIUM LOCK POPUP ---------- */

export function PremiumLockPopup({ feature, onClose }) {
  const { t } = useTranslation();
  return (
    <ModalShell title={t("carto.premiumLockTitle")} subtitle={t("carto.premiumLockSubtitle")} onClose={onClose} testId="premium-lock-popup">
      <div className="space-y-4 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-arcane/20 border border-arcane/50 mx-auto">
          <Feather className="w-8 h-8 text-arcane" />
        </div>
        <h4 className="font-display text-xl text-arcane">{feature?.title || t("carto.premiumOnly")}</h4>
        <p className="font-serif-fantasy italic text-parchment/90">
          « {feature?.description || t("carto.premiumLockQuote").replace(/^«\s?|\s?»$/g, "")} »
        </p>
        <div className="pt-4 flex flex-col sm:flex-row gap-2 justify-center">
          <a href="/store" data-testid="premium-upgrade-link">
            <Button variant="arcane" size="sm">{t("carto.gotoStore")}</Button>
          </a>
          <Button variant="secondary" size="sm" onClick={onClose}>{t("carto.close")}</Button>
        </div>
      </div>
    </ModalShell>
  );
}
